import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Banknote,
  BarChart3,
  Calendar,
  CreditCard,
  Edit3,
  Eye,
  FileText,
  Filter,
  Package2,
  PlusCircle,
  Search,
  Trash2,
  Truck,
} from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { samedayStatusConfig } from "../../config/sameday.status.config";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useToast } from "../../hooks/useToast";
import { OrderStatus, PaymentStatus } from "../../prisma-types";
import type {
  BulkDeleteRequest,
  BulkDeleteResponse,
  CreateDraftData,
  DraftListResponse,
  DraftStatsResponse,
} from "../../types/drafts";
import { Order, OrderFilters } from "../../types/orders";
import { formatDate, formatPrice } from "../../utils/format";

export interface DraftOrdersResponse {
  data: Order[];
  total: number;
  page: number;
  pageSize: number;
}

const STATUS_TRANSLATIONS: Record<OrderStatus, string> = {
  PENDING: "În așteptare",
  PAID: "Plătită",
  PROCESSING: "În procesare",
  SHIPPED: "Expediată",
  DELIVERED: "Livrată",
  CANCELLED: "Anulată",
};

const PAYMENT_STATUS_TRANSLATIONS: Record<PaymentStatus, string> = {
  PENDING: "În așteptare",
  PROCESSING: "În procesare",
  COMPLETED: "Finalizată",
  FAILED: "Eșuată",
  REFUNDED: "Rambursată",
};

const getStatusColor = (statusId: number): string => {
  const status = samedayStatusConfig.statusMapping[statusId];
  switch (status) {
    case "DELIVERED":
      return "bg-green-100 text-green-800";
    case "IN_TRANSIT":
      return "bg-yellow-100 text-yellow-800";
    case "FAILED":
      return "bg-red-100 text-red-800";
    case "RETURNED":
      return "bg-purple-100 text-purple-800";
    case "CANCELED":
      return "bg-red-100 text-red-800";
    case "REGISTERED":
    default:
      return "bg-blue-100 text-blue-800";
  }
};

const ORDER_STATUSES = Object.values(OrderStatus);
const PAYMENT_STATUSES = Object.values(PaymentStatus);

const paymentStatusColors: Record<PaymentStatus, string> = {
  PENDING: "bg-yellow-100 text-yellow-800",
  PROCESSING: "bg-blue-100 text-blue-800",
  COMPLETED: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
  REFUNDED: "bg-gray-100 text-gray-800",
};

export function DraftsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(10);
  const [sortBy, setSortBy] = React.useState<"createdAt" | "email" | "name">(
    "createdAt"
  );
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");

  // Form pentru adăugare draft nou
  const [newEmail, setNewEmail] = React.useState("");
  const [newName, setNewName] = React.useState("");
  const [newPhone, setNewPhone] = React.useState("");
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [phoneError, setPhoneError] = React.useState<string | null>(null);

  // Selecție multiplă pentru ștergere în lot
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);

  // Draft Orders State
  const [draftOrdersFilters, setDraftOrdersFilters] =
    React.useState<OrderFilters>({
      search: "",
      status: undefined,
      paymentStatus: undefined,
      shippingStatus: undefined,
      dateFrom: "",
      dateTo: "",
    });
  const [draftOrdersPage, setDraftOrdersPage] = React.useState(1);
  const [draftOrdersPageSize] = React.useState(10);

  // Validări
  const isValidEmail = React.useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(newEmail);
  }, [newEmail]);

  const isValidPhone = React.useMemo(() => {
    return newPhone.length === 10 && newPhone.startsWith("07");
  }, [newPhone]);

  const isFormValid = React.useMemo(() => {
    return newName.trim() && isValidEmail && isValidPhone;
  }, [newName, isValidEmail, isValidPhone]);

  // Fetch drafts cu căutare și sortare
  const { data, isLoading } = useQuery<DraftListResponse>({
    queryKey: ["drafts", page, pageSize, search, sortBy, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) {
        // Dacă avem căutare, folosim endpoint-ul de search
        params.append("q", search);
        const response = await fetchWithAuth(
          `/api/admin/drafts/search?${params}`
        );
        if (!response.ok) throw new Error("Eroare la căutarea draft-urilor");
        return response.json();
      } else {
        // Altfel folosim endpoint-ul normal cu paginare și sortare
        params.append("page", page.toString());
        params.append("limit", pageSize.toString());
        params.append("sortBy", sortBy);
        params.append("sortOrder", sortOrder);

        const response = await fetchWithAuth(`/api/admin/drafts?${params}`);
        if (!response.ok) throw new Error("Eroare la încărcarea draft-urilor");
        return response.json();
      }
    },
  });

  console.log("drafts response", data);

  // Fetch statistici
  const { data: statsData } = useQuery<DraftStatsResponse>({
    queryKey: ["drafts-stats"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/drafts/stats");
      if (!response.ok) throw new Error("Eroare la încărcarea statisticilor");
      return response.json();
    },
    staleTime: 60 * 1000, // 1 minut
  });

  // Fetch draft orders
  const { data: draftOrdersResponse, isLoading: isDraftOrdersLoading } =
    useQuery<DraftOrdersResponse>({
      refetchInterval: 15000, // Refetch every 15 seconds
      queryKey: [
        "draft-orders",
        draftOrdersFilters,
        draftOrdersPage,
        draftOrdersPageSize,
      ],
      queryFn: async () => {
        const params = new URLSearchParams();
        if (draftOrdersFilters.search)
          params.append("search", draftOrdersFilters.search);
        if (draftOrdersFilters.status)
          params.append("status", draftOrdersFilters.status);
        if (draftOrdersFilters.paymentStatus)
          params.append("paymentStatus", draftOrdersFilters.paymentStatus);
        if (draftOrdersFilters.shippingStatus)
          params.append("shippingStatus", draftOrdersFilters.shippingStatus);
        if (draftOrdersFilters.dateFrom)
          params.append("dateFrom", draftOrdersFilters.dateFrom);
        if (draftOrdersFilters.dateTo)
          params.append("dateTo", draftOrdersFilters.dateTo);
        params.append("page", draftOrdersPage.toString());
        params.append("limit", draftOrdersPageSize.toString());

        const response = await fetchWithAuth(
          `/api/admin/draft-orders?${params}`
        );

        console.log("draft-orders raw response", response);

        if (!response.ok) {
          throw new Error("Eroare la încărcarea comenzilor draft");
        }

        // Parse JSON data
        const jsonData = await response.json();
        console.log("draft-orders parsed data", jsonData);

        return jsonData;
      },
    });

  console.log("draft-orders response", draftOrdersResponse);

  // Mutations pentru CRUD operations
  const createMutation = useMutation({
    mutationFn: async (payload: CreateDraftData) => {
      const response = await fetchWithAuth("/api/drafts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Eroare la crearea draft-ului");
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["drafts"] });
      queryClient.invalidateQueries({ queryKey: ["drafts-stats"] });
      setNewEmail("");
      setNewName("");
      setNewPhone("");
      toast.success(data.message || "Draft creat cu succes");
    },
    onError: (error: any) => {
      toast.error(error.message || "Eroare la crearea draft-ului");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetchWithAuth(`/api/admin/drafts/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Eroare la ștergerea draft-ului");
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["drafts"] });
      queryClient.invalidateQueries({ queryKey: ["drafts-stats"] });
      toast.success(data.message || "Draft șters cu succes");
    },
    onError: (error: any) => {
      toast.error(error.message || "Eroare la ștergerea draft-ului");
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (payload: BulkDeleteRequest) => {
      const response = await fetchWithAuth("/api/admin/drafts/bulk-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Eroare la ștergerea draft-urilor");
      return response.json();
    },
    onSuccess: (data: BulkDeleteResponse) => {
      queryClient.invalidateQueries({ queryKey: ["drafts"] });
      queryClient.invalidateQueries({ queryKey: ["drafts-stats"] });
      setSelectedIds([]);
      toast.success(`${data.deletedCount} draft-uri șterse cu succes`);
    },
    onError: (error: any) => {
      toast.error(error.message || "Eroare la ștergerea draft-urilor");
    },
  });

  // Handlers
  const handleCreate = () => {
    if (!isFormValid) return;
    createMutation.mutate({
      email: newEmail.trim(),
      name: newName.trim(),
      phone: newPhone.trim(),
    });
  };

  const handleDelete = (id: number) => {
    if (!id) return;
    const confirmDelete = window.confirm("Sigur vrei să ștergi acest draft?");
    if (!confirmDelete) return;
    deleteMutation.mutate(id);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    const confirmDelete = window.confirm(
      `Sigur vrei să ștergi ${selectedIds.length} draft-uri selectate?`
    );
    if (!confirmDelete) return;
    bulkDeleteMutation.mutate({ ids: selectedIds });
  };

  const handleSort = (newSortBy: "createdAt" | "email" | "name") => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const handleSelectAll = () => {
    if (selectedIds.length === data?.data.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data?.data.map((d) => d.id) || []);
    }
  };

  const handleSelectItem = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds((prev) => [...prev, id]);
    }
  };

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <FileText className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Draft-uri</h1>
            <div className="flex items-center gap-2 mt-1">
              <AlertCircle className="w-4 h-4 text-gray-500" />
              <p className="text-sm text-gray-600">
                Gestionează draft-urile din platformă.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistici */}
      {statsData?.data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-700">
                  Total Draft-uri
                </h3>
                <p className="text-3xl font-bold text-blue-600">
                  {statsData.data.total}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Astăzi</h3>
                <p className="text-3xl font-bold text-green-600">
                  {statsData.data.today}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center gap-3">
              <Edit3 className="w-8 h-8 text-purple-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-700">
                  Săptămâna aceasta
                </h3>
                <p className="text-3xl font-bold text-purple-600">
                  {statsData.data.thisWeek}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filtre și căutare */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="font-semibold text-gray-900">Filtre și căutare</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              placeholder="Caută după nume, email sau telefon..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {selectedIds.length > 0 && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                className="flex items-center gap-2 text-red-600 border-red-600 hover:bg-red-50"
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isPending}
              >
                <Trash2 className="w-4 h-4" />
                Șterge {selectedIds.length} selectate
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Formular pentru adăugare draft nou */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center gap-2 mb-4">
          <PlusCircle className="w-5 h-5 text-gray-600" />
          <h2 className="font-semibold text-gray-900">Adaugă draft nou</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <Input
              type="text"
              placeholder="Nume complet"
              value={newName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewName(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && isFormValid) handleCreate();
              }}
            />
          </div>

          <div className="flex flex-col gap-1">
            <Input
              type="email"
              placeholder="Email"
              value={newEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setNewEmail(e.target.value);
                setEmailError(
                  e.target.value && !isValidEmail ? "Email invalid" : null
                );
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && isFormValid) handleCreate();
              }}
            />
            {emailError && (
              <span className="text-xs text-red-600">{emailError}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Input
              type="tel"
              placeholder="Telefon (ex: 07xxxxxxxx)"
              value={newPhone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                let value = e.target.value.replace(/\D/g, "");

                if (value === "") {
                  setNewPhone("");
                  setPhoneError(null);
                  return;
                }

                if (value.length === 1 && value !== "0") {
                  value = "0" + value;
                }

                if (
                  value.length === 2 &&
                  value[0] === "0" &&
                  value[1] !== "7"
                ) {
                  value = "07";
                }

                if (value.length > 10) {
                  value = value.slice(0, 10);
                }

                setNewPhone(value);
                setPhoneError(
                  value.length === 10 && value.startsWith("07")
                    ? null
                    : "Număr invalid. Format: 07xxxxxxxx (10 cifre)"
                );
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && isFormValid) handleCreate();
              }}
            />
            {phoneError && (
              <span className="text-xs text-red-600">{phoneError}</span>
            )}
          </div>

          <Button
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={handleCreate}
            disabled={createMutation.isPending || !isFormValid}
          >
            <PlusCircle className="w-4 h-4" />
            Creează draft
          </Button>
        </div>
      </Card>

      {/* Tabel draft-uri */}
      <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <input
                  type="checkbox"
                  checked={
                    selectedIds.length === data?.data.length &&
                    data?.data.length > 0
                  }
                  onChange={handleSelectAll}
                  className="rounded border-gray-300"
                />
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => handleSort("name")}
              >
                <div className="flex items-center gap-1">
                  Nume
                  {sortBy === "name" && (
                    <span className="text-xs">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => handleSort("email")}
              >
                <div className="flex items-center gap-1">
                  Email
                  {sortBy === "email" && (
                    <span className="text-xs">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </TableHead>
              <TableHead>Telefon</TableHead>
              <TableHead>Cărți</TableHead>
              <TableHead>Total</TableHead>
              <TableHead
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => handleSort("createdAt")}
              >
                <div className="flex items-center gap-1">
                  Data creării
                  {sortBy === "createdAt" && (
                    <span className="text-xs">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </div>
              </TableHead>
              <TableHead>Actualizat</TableHead>
              <TableHead>Este nou</TableHead>
              <TableHead>Comenzi</TableHead>
              <TableHead>Acțiuni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-10">
                  Se încarcă...
                </TableCell>
              </TableRow>
            ) : (data?.data?.length ?? 0) === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-10">
                  {search
                    ? `Nu s-au găsit draft-uri pentru "${search}"`
                    : "Nu există draft-uri"}
                </TableCell>
              </TableRow>
            ) : (
              data?.data.map((draft) => (
                <TableRow key={draft.id} className="hover:bg-gray-50">
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(draft.id)}
                      onChange={() => handleSelectItem(draft.id)}
                      className="rounded border-gray-300"
                    />
                  </TableCell>
                  <TableCell className="font-medium">{draft.name}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {draft.email}
                  </TableCell>
                  <TableCell className="font-mono">{draft.phone}</TableCell>
                  <TableCell>
                    {draft.cartItems && draft.cartItems.length > 0 ? (
                      <div className="flex flex-col gap-1 max-w-xs">
                        {draft.cartItems.map((item, index) => (
                          <div
                            key={index}
                            className="text-sm flex items-center gap-2"
                          >
                            <Badge variant="secondary" className="text-xs">
                              {item.quantity}x
                            </Badge>
                            <span className="truncate" title={item.title}>
                              {item.title}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="font-semibold">
                    {draft.cartItemsTotalPrice
                      ? formatPrice(draft.cartItemsTotalPrice)
                      : "-"}
                  </TableCell>
                  <TableCell>{formatDate(draft.createdAt)}</TableCell>
                  <TableCell>{formatDate(draft.updatedAt)}</TableCell>
                  <TableCell>
                    {draft.isNew ? (
                      <Badge className="bg-green-100 text-green-800 px-3 py-1.5 rounded-full shadow-sm ring-1 ring-green-200">
                        Nou
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full shadow-sm ring-1 ring-gray-200">
                        Vechi
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {draft.orderIds && draft.orderIds.length > 0 ? (
                      <div className="grid grid-cols-4 gap-1 ">
                        {draft.orderIds.map((oid) => (
                          <Link
                            key={oid}
                            to={`/orders/${oid}/`}
                            className="inline-flex items-center justify-center  rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700"
                            title={`Comanda #${oid}`}
                          >
                            {oid}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 text-red-600 border-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(draft.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                      Șterge
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Paginare */}
      {data && data.pagination && data.pagination.pages > 1 && (
        <div className="flex justify-center mt-4">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600">
                Pagina {page} din {data.pagination.pages}
              </span>
            </div>
            <Button
              variant="outline"
              className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= (data.pagination.pages || 1)}
            >
              Următor
            </Button>
          </div>
        </div>
      )}

      {/* Draft Orders Section */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-2">
          <Package2 className="w-8 h-8 text-orange-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Comenzi Draft</h2>
            <div className="flex items-center gap-2 mt-1">
              <AlertCircle className="w-4 h-4 text-gray-500" />
              <p className="text-sm text-gray-600">
                Comenzile în stadiu de draft.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Draft Orders Filters */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Filtre Comenzi Draft</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              placeholder="Caută după ID, client, email..."
              value={draftOrdersFilters.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraftOrdersFilters((f) => ({ ...f, search: e.target.value }))
              }
            />
          </div>

          <Select
            value={draftOrdersFilters.status ?? "ALL"}
            onValueChange={(value) =>
              setDraftOrdersFilters((f) => ({
                ...f,
                status: value === "ALL" ? undefined : (value as OrderStatus),
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate statusurile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate statusurile</SelectItem>
              {ORDER_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {STATUS_TRANSLATIONS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={draftOrdersFilters.paymentStatus ?? "ALL"}
            onValueChange={(value) =>
              setDraftOrdersFilters((f) => ({
                ...f,
                paymentStatus:
                  value === "ALL" ? undefined : (value as PaymentStatus),
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate plățile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate plățile</SelectItem>
              {PAYMENT_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {PAYMENT_STATUS_TRANSLATIONS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={draftOrdersFilters.shippingStatus ?? "ALL"}
            onValueChange={(value) =>
              setDraftOrdersFilters((f) => ({
                ...f,
                shippingStatus: value === "ALL" ? undefined : value,
              }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Toate livrările" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Toate livrările</SelectItem>
              {Object.entries(samedayStatusConfig.statusLabels).map(
                ([id, label]) => (
                  <SelectItem key={id} value={id}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>

          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              type="date"
              value={draftOrdersFilters.dateFrom}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraftOrdersFilters((f) => ({
                  ...f,
                  dateFrom: e.target.value,
                }))
              }
            />
          </div>

          <div className="relative">
            <Calendar className="w-4 h-4 absolute left-3 top-3 text-gray-500" />
            <Input
              className="pl-9"
              type="date"
              value={draftOrdersFilters.dateTo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraftOrdersFilters((f) => ({ ...f, dateTo: e.target.value }))
              }
            />
          </div>
        </div>
      </Card>

      {/* Draft Orders Table */}
      <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Plată</TableHead>
              <TableHead>Livrare</TableHead>
              <TableHead>AWB</TableHead>
              <TableHead>Factură</TableHead>
              <TableHead>Acțiuni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isDraftOrdersLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  Se încarcă...
                </TableCell>
              </TableRow>
            ) : (draftOrdersResponse?.data?.length ?? 0) === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  Nu există comenzi draft
                </TableCell>
              </TableRow>
            ) : (
              draftOrdersResponse?.data?.map((order: Order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {order.id}
                      <Badge
                        variant="outline"
                        className={`px-2 py-0.5 flex items-center gap-1 ${
                          order.payment?.method === "NETOPIA"
                            ? "bg-blue-50 text-blue-700 ring-1 ring-blue-700/10"
                            : "bg-green-50 text-green-700 ring-1 ring-green-700/10"
                        }`}
                      >
                        {order.payment?.method === "NETOPIA" ? (
                          <>
                            <CreditCard className="w-3 h-3" />
                            Card
                          </>
                        ) : (
                          <>
                            <Banknote className="w-3 h-3" />
                            Ramburs
                          </>
                        )}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(order.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span>
                        {order.metadata?.customerName || order.user.name}
                      </span>
                      <span className="text-sm text-gray-500">
                        {order.metadata?.customerEmail || order.user.email}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{formatPrice(order.totalPrice)}</TableCell>
                  <TableCell>
                    <Badge
                      className={`px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset ${
                        order.status === "CANCELLED"
                          ? "bg-red-100 text-red-800 ring-red-200"
                          : paymentStatusColors[
                              (order.payment?.status ||
                                "PENDING") as PaymentStatus
                            ] + " ring-current/20"
                      }`}
                    >
                      {order.status === "CANCELLED"
                        ? "Anulată"
                        : PAYMENT_STATUS_TRANSLATIONS[
                            (order.payment?.status ||
                              "PENDING") as PaymentStatus
                          ]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset ${
                        order.status === "CANCELLED"
                          ? "bg-red-100 text-red-800 ring-red-200"
                          : order.awbNumber
                          ? getStatusColor(
                              order.metadata?.shippingStatusDetails?.statusId ||
                                1
                            ) + " ring-current/20"
                          : "bg-yellow-100 text-yellow-800 ring-yellow-200"
                      }`}
                    >
                      {order.status === "CANCELLED"
                        ? "Anulată"
                        : order.awbNumber
                        ? order.metadata?.shippingStatusDetails?.statusId
                          ? samedayStatusConfig.statusLabels[
                              order.metadata.shippingStatusDetails.statusId
                            ]
                          : "Comandă înregistrată"
                        : order.status === "PENDING"
                        ? "În așteptare"
                        : order.status === "PAID"
                        ? "Plătită"
                        : order.status === "PROCESSING"
                        ? "În procesare"
                        : "În așteptare"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {order.awbNumber ? (
                      <Button
                        variant="link"
                        className="flex items-center gap-1"
                        onClick={() => window.open(order.awbUrl, "_blank")}
                      >
                        <Truck className="w-4 h-4" />
                        {order.awbNumber}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {order.invoiceNumber ? (
                      <Button
                        variant="link"
                        className="flex items-center gap-1"
                        onClick={() => window.open(order.invoiceUrl, "_blank")}
                      >
                        <FileText className="w-4 h-4" />
                        {order.invoiceSeries}/{order.invoiceNumber}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="link" asChild>
                        <Link
                          to={`/orders/${order.id}/`}
                          className="flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          Detalii
                        </Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Draft Orders Pagination */}
      {draftOrdersResponse &&
        draftOrdersResponse.total > draftOrdersPageSize && (
          <div className="flex justify-center mt-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
                onClick={() => setDraftOrdersPage((p) => Math.max(1, p - 1))}
                disabled={draftOrdersPage === 1}
              >
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-600">
                  Pagina {draftOrdersPage} din{" "}
                  {Math.ceil(draftOrdersResponse.total / draftOrdersPageSize)}
                </span>
              </div>
              <Button
                variant="outline"
                className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
                onClick={() => setDraftOrdersPage((p) => p + 1)}
                disabled={
                  draftOrdersPage >=
                  Math.ceil(draftOrdersResponse.total / draftOrdersPageSize)
                }
              >
                Următor
              </Button>
            </div>
          </div>
        )}
    </div>
  );
}
